import { useState } from 'react'
import {
  Shield,
  Plus,
  Pencil,
  Trash2,
  X,
  Check,
  Loader2,
  Search,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { toast } from 'sonner'
import { kontakteAPI } from '@/utils/api'
import type { KontaktDetail, Versicherungspolice, KontaktSearchResult } from '@/utils/api'

interface VersicherungenTabProps {
  kontakt: KontaktDetail
  onRefresh: () => void
}

export function VersicherungenTab({ kontakt, onRefresh }: VersicherungenTabProps) {
  const [isAdding, setIsAdding] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [deletingId, setDeletingId] = useState<number | null>(null)

  const [formData, setFormData] = useState({
    rsv: null as number | null,
    rsv_name: '',
    versicherungsnummer: '',
    deckungsart: '',
    gueltig_von: '',
    gueltig_bis: '',
    is_active: true,
    notes: '',
  })

  // RSV search state
  const [rsvSearch, setRsvSearch] = useState('')
  const [rsvResults, setRsvResults] = useState<KontaktSearchResult[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [showRsvDropdown, setShowRsvDropdown] = useState(false)

  const resetForm = () => {
    setFormData({
      rsv: null,
      rsv_name: '',
      versicherungsnummer: '',
      deckungsart: '',
      gueltig_von: '',
      gueltig_bis: '',
      is_active: true,
      notes: '',
    })
    setRsvSearch('')
    setRsvResults([])
  }

  const startEdit = (police: Versicherungspolice) => {
    setEditingId(police.id)
    setFormData({
      rsv: police.rsv,
      rsv_name: police.rsv_name,
      versicherungsnummer: police.versicherungsnummer,
      deckungsart: police.deckungsart || '',
      gueltig_von: police.gueltig_von || '',
      gueltig_bis: police.gueltig_bis || '',
      is_active: police.is_active,
      notes: police.notes || '',
    })
    setRsvSearch(police.rsv_name)
  }

  const handleRsvSearch = async (query: string) => {
    setRsvSearch(query)
    if (query.length < 2) {
      setRsvResults([])
      setShowRsvDropdown(false)
      return
    }

    setIsSearching(true)
    try {
      const results = await kontakteAPI.searchKontakte(query, { tag: 'rsv', limit: 10 })
      setRsvResults(results)
      setShowRsvDropdown(true)
    } catch (err) {
      console.error('Failed to search RSV:', err)
    } finally {
      setIsSearching(false)
    }
  }

  const selectRsv = (rsv: KontaktSearchResult) => {
    setFormData({ ...formData, rsv: rsv.id, rsv_name: rsv.display_name })
    setRsvSearch(rsv.display_name)
    setShowRsvDropdown(false)
    setRsvResults([])
  }

  const handleSave = async () => {
    if (!formData.rsv || !formData.versicherungsnummer) {
      toast.error('Bitte RSV und Versicherungsnummer angeben')
      return
    }

    setIsSaving(true)
    try {
      const saveData = {
        rsv: formData.rsv,
        versicherungsnummer: formData.versicherungsnummer,
        deckungsart: formData.deckungsart || null,
        gueltig_von: formData.gueltig_von || null,
        gueltig_bis: formData.gueltig_bis || null,
        is_active: formData.is_active,
        notes: formData.notes || null,
      }

      if (editingId) {
        await kontakteAPI.updateVersicherung(kontakt.id, editingId, saveData)
        toast.success('Versicherung aktualisiert')
      } else {
        await kontakteAPI.createVersicherung(kontakt.id, saveData)
        toast.success('Versicherung hinzugefügt')
      }
      onRefresh()
      resetForm()
      setIsAdding(false)
      setEditingId(null)
    } catch (err) {
      console.error('Failed to save versicherung:', err)
      toast.error('Versicherung konnte nicht gespeichert werden')
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async (policeId: number) => {
    setDeletingId(policeId)
    try {
      await kontakteAPI.deleteVersicherung(kontakt.id, policeId)
      toast.success('Versicherung gelöscht')
      onRefresh()
    } catch (err) {
      console.error('Failed to delete versicherung:', err)
      toast.error('Versicherung konnte nicht gelöscht werden')
    } finally {
      setDeletingId(null)
    }
  }

  const cancelEdit = () => {
    resetForm()
    setIsAdding(false)
    setEditingId(null)
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return '—'
    return new Date(dateString).toLocaleDateString('de-DE')
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <Shield className="h-5 w-5 text-slate-400" />
          Rechtsschutzversicherungen
          {kontakt.versicherungen.length > 0 && (
            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-slate-100 text-slate-600">
              {kontakt.versicherungen.length}
            </span>
          )}
        </h2>
        {!isAdding && !editingId && (
          <Button variant="outline" size="sm" onClick={() => setIsAdding(true)}>
            <Plus className="h-4 w-4 mr-1" />
            Hinzufügen
          </Button>
        )}
      </div>

      <div className="space-y-4">
        {kontakt.versicherungen.map((police) =>
          editingId === police.id ? (
            <VersicherungForm
              key={police.id}
              formData={formData}
              setFormData={setFormData}
              rsvSearch={rsvSearch}
              rsvResults={rsvResults}
              showRsvDropdown={showRsvDropdown}
              isSearching={isSearching}
              onRsvSearch={handleRsvSearch}
              onSelectRsv={selectRsv}
              setShowRsvDropdown={setShowRsvDropdown}
              onSave={handleSave}
              onCancel={cancelEdit}
              isSaving={isSaving}
            />
          ) : (
            <div
              key={police.id}
              className="p-4 rounded-lg bg-slate-50 border border-slate-200 group"
            >
              <div className="flex items-start justify-between">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-slate-900">
                      {police.rsv_name}
                    </span>
                    {police.is_active ? (
                      <span className="px-2 py-0.5 text-xs font-medium rounded bg-green-100 text-green-700">
                        Aktiv
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 text-xs font-medium rounded bg-slate-200 text-slate-600">
                        Inaktiv
                      </span>
                    )}
                    {!police.is_valid && police.is_active && (
                      <span className="px-2 py-0.5 text-xs font-medium rounded bg-amber-100 text-amber-700">
                        Abgelaufen
                      </span>
                    )}
                  </div>

                  <dl className="grid grid-cols-2 gap-x-8 gap-y-1 text-sm">
                    <div className="flex gap-2">
                      <dt className="text-slate-500">Versicherungsnummer:</dt>
                      <dd className="text-slate-900 font-mono">
                        {police.versicherungsnummer}
                      </dd>
                    </div>
                    {police.deckungsart && (
                      <div className="flex gap-2">
                        <dt className="text-slate-500">Deckungsart:</dt>
                        <dd className="text-slate-900">{police.deckungsart}</dd>
                      </div>
                    )}
                    <div className="flex gap-2">
                      <dt className="text-slate-500">Gültig von:</dt>
                      <dd className="text-slate-900">
                        {formatDate(police.gueltig_von)}
                      </dd>
                    </div>
                    <div className="flex gap-2">
                      <dt className="text-slate-500">Gültig bis:</dt>
                      <dd className="text-slate-900">
                        {formatDate(police.gueltig_bis)}
                      </dd>
                    </div>
                  </dl>

                  {police.notes && (
                    <p className="text-sm text-slate-500 italic mt-2">
                      {police.notes}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => startEdit(police)}
                    className="h-8 w-8 p-0"
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(police.id)}
                    disabled={deletingId === police.id}
                    className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                  >
                    {deletingId === police.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )
        )}

        {isAdding && (
          <VersicherungForm
            formData={formData}
            setFormData={setFormData}
            rsvSearch={rsvSearch}
            rsvResults={rsvResults}
            showRsvDropdown={showRsvDropdown}
            isSearching={isSearching}
            onRsvSearch={handleRsvSearch}
            onSelectRsv={selectRsv}
            setShowRsvDropdown={setShowRsvDropdown}
            onSave={handleSave}
            onCancel={cancelEdit}
            isSaving={isSaving}
          />
        )}

        {kontakt.versicherungen.length === 0 && !isAdding && (
          <div className="flex flex-col items-center justify-center py-8">
            <Shield className="h-12 w-12 text-slate-200 mb-3" />
            <p className="text-slate-500 text-center">
              Keine Rechtsschutzversicherungen hinterlegt.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

function VersicherungForm({
  formData,
  setFormData,
  rsvSearch,
  rsvResults,
  showRsvDropdown,
  isSearching,
  onRsvSearch,
  onSelectRsv,
  setShowRsvDropdown,
  onSave,
  onCancel,
  isSaving,
}: {
  formData: any
  setFormData: (data: any) => void
  rsvSearch: string
  rsvResults: KontaktSearchResult[]
  showRsvDropdown: boolean
  isSearching: boolean
  onRsvSearch: (query: string) => void
  onSelectRsv: (rsv: KontaktSearchResult) => void
  setShowRsvDropdown: (show: boolean) => void
  onSave: () => void
  onCancel: () => void
  isSaving: boolean
}) {
  return (
    <div className="p-4 rounded-lg border border-slate-300 bg-slate-50 space-y-4">
      {/* RSV Search */}
      <div className="space-y-2 relative">
        <Label>Rechtsschutzversicherung *</Label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            value={rsvSearch}
            onChange={(e) => onRsvSearch(e.target.value)}
            onFocus={() => rsvResults.length > 0 && setShowRsvDropdown(true)}
            placeholder="RSV suchen..."
            className="pl-9"
          />
          {isSearching && (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 animate-spin" />
          )}
        </div>

        {/* RSV Dropdown */}
        {showRsvDropdown && rsvResults.length > 0 && (
          <div className="absolute z-10 w-full mt-1 bg-white rounded-lg border border-slate-200 shadow-lg max-h-60 overflow-auto">
            {rsvResults.map((rsv) => (
              <button
                key={rsv.id}
                onClick={() => onSelectRsv(rsv)}
                className="w-full px-4 py-2 text-left hover:bg-slate-50 flex items-center justify-between"
              >
                <span className="font-medium text-slate-900">{rsv.display_name}</span>
                {rsv.iata_code && (
                  <span className="text-xs font-mono text-slate-500">
                    {rsv.iata_code}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        {formData.rsv && (
          <p className="text-sm text-green-600">
            Ausgewählt: {formData.rsv_name}
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Versicherungsnummer *</Label>
          <Input
            value={formData.versicherungsnummer}
            onChange={(e) =>
              setFormData({ ...formData, versicherungsnummer: e.target.value })
            }
            placeholder="12345678"
          />
        </div>
        <div className="space-y-2">
          <Label>Deckungsart</Label>
          <Input
            value={formData.deckungsart}
            onChange={(e) =>
              setFormData({ ...formData, deckungsart: e.target.value })
            }
            placeholder="Verkehr, Privat, etc."
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Gültig von</Label>
          <Input
            type="date"
            value={formData.gueltig_von}
            onChange={(e) =>
              setFormData({ ...formData, gueltig_von: e.target.value })
            }
          />
        </div>
        <div className="space-y-2">
          <Label>Gültig bis</Label>
          <Input
            type="date"
            value={formData.gueltig_bis}
            onChange={(e) =>
              setFormData({ ...formData, gueltig_bis: e.target.value })
            }
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Notizen</Label>
        <Textarea
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          placeholder="Weitere Informationen zur Police..."
          rows={2}
        />
      </div>

      <div className="flex items-center justify-between pt-2">
        <label className="flex items-center gap-2 cursor-pointer">
          <Switch
            checked={formData.is_active}
            onCheckedChange={(checked) =>
              setFormData({ ...formData, is_active: checked })
            }
          />
          <span className="text-sm text-slate-700">Police aktiv</span>
        </label>

        <div className="flex gap-2">
          <Button variant="outline" onClick={onCancel} disabled={isSaving}>
            <X className="h-4 w-4 mr-1" />
            Abbrechen
          </Button>
          <Button
            onClick={onSave}
            disabled={isSaving || !formData.rsv || !formData.versicherungsnummer}
          >
            {isSaving ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Check className="h-4 w-4 mr-1" />
            )}
            Speichern
          </Button>
        </div>
      </div>
    </div>
  )
}
